import type { EntregaCatalogRepositoryPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaPorts.ts';
import { warmupEntregaMapTilesFromCatalog } from '@src/modules/entrega-paquetes/infrastructure/driven/entregaMapTileWarmup.ts';
import { loadEntregaPdfTemplateBytes, loadReposicionPdfTemplateBytes } from '@src/modules/platform/pdf/pdfTemplateSources.ts';
import { SHELL_MAIN_PAGE_MANIFEST } from '@src/modules/shell-routing/domain/shellFeatureManifest.ts';
import { ensureShellPageComponentLoaded } from '@src/modules/shell-routing/infrastructure/shellLazyPageLoad.ts';

export type OfflineAssetWarmupOpts = {
  entregaCatalog?: EntregaCatalogRepositoryPort;
  onMapProgress?: (processed: number, total: number) => void;
};

function offlineAssetWarmupEnabled(): boolean {
  return (import.meta.env.ADMIN_OFFLINE_SERVICE_WORKER ?? '1') !== '0';
}

/** Fire-and-forget shell/PDF prefetch after login or session restore (no network). */
export function scheduleBackgroundOfflineWarmup(opts?: OfflineAssetWarmupOpts): void {
  if (!offlineAssetWarmupEnabled()) return;
  void warmupShellOfflineAssets(opts).catch((e) => {
    // oxlint-disable-next-line no-console -- best-effort background prefetch
    console.warn('[warmup] background offline prefetch failed', e);
  });
}

/** Prefetch lazy routes and PDF templates offline; OSM map tiles only when online. */
export async function warmupShellOfflineAssets(opts?: OfflineAssetWarmupOpts): Promise<void> {
  if (!offlineAssetWarmupEnabled()) return;
  await Promise.all([
    ...listOfflineWarmupPageIds().map((pageId) => ensureShellPageComponentLoaded(pageId)),
    loadEntregaPdfTemplateBytes(),
    loadReposicionPdfTemplateBytes(),
  ]);
  if (opts?.entregaCatalog && globalThis.navigator?.onLine !== false) {
    await warmupEntregaMapTilesFromCatalog(opts.entregaCatalog, { onProgress: opts.onMapProgress });
  }
}

/** @internal test hook */
export function listOfflineWarmupPageIds(): readonly string[] {
  return SHELL_MAIN_PAGE_MANIFEST.map((entry) => entry.pageId);
}
